import { Type, type SchemaOptions } from '@sinclair/typebox';
import { validateEnumTranslations } from '../../safety_checks';

// Les énumérations promo vivent ICI (et non dans pro_promo_code.ts) : les schémas de formulaire
// de pro_promo_code.ts consomment les unions libellées ci-dessous, ce fichier ne doit donc rien
// importer de pro_promo_code.ts (cycle d'évaluation ESM). pro_promo_code.ts les ré-exporte.

export const TRANSLATE_KEY_PRO_PROMO_CODE_KIND = 'PRO_PROMO_CODE_KIND_';
export const TRANSLATE_KEY_PRO_PROMO_CODE_DISTRIBUTION = 'PRO_PROMO_CODE_DISTRIBUTION_';
export const TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY = 'BOOKING_PROMO_INELIGIBILITY_';
export const TRANSLATE_KEY_PRO_PROMO_CODE_TRIGGER = 'PRO_PROMO_CODE_TRIGGER_';
export const TRANSLATE_KEY_PRO_PROMO_CODE_STATUS = 'PRO_PROMO_CODE_STATUS_';
export const TRANSLATE_KEY_PRO_PROMO_CODE_CUSTOMER_SCOPE = 'PRO_PROMO_CODE_CUSTOMER_SCOPE_';
export const TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS = 'PRO_PROMOTION_DISPLAY_STATUS_';
export const TRANSLATE_KEY_PRO_PROMO_CODE_WINDOW_BASIS = 'PRO_PROMO_CODE_WINDOW_BASIS_';

//⚠️IMPACT attention changer dans le fichier prisma l'enum ProPromoCodeKind
export enum ProPromoCodeKind {
  PERCENT = 'PERCENT',
  FIXED_AMOUNT_MINOR = 'FIXED_AMOUNT_MINOR',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum ProPromoCodeDistribution
export enum ProPromoCodeDistribution {
  // Un seul code partagé, saisi par tous les clients.
  SHARED_CODE = 'SHARED_CODE',
  // Un code unique par client, généré pour l'audience d'une campagne marketing.
  PERSONAL_CODES = 'PERSONAL_CODES',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum ProPromoCodeTrigger
/** Déclenchement d'une promotion (lot 2) : automatique (sans code) ou par code saisi. */
export enum ProPromoCodeTrigger {
  AUTO = 'AUTO',
  CODE = 'CODE',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum ProPromoCodeStatus
/** Statut d'une promotion ; « terminée » n'est pas stocké (fin de validité passée ou quota atteint). */
export enum ProPromoCodeStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  ARCHIVED = 'ARCHIVED',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum ProPromoCodeCustomerScope
/** Clients concernés : tous, nouveaux clients, clients portant au moins un des tags choisis. */
export enum ProPromoCodeCustomerScope {
  ALL = 'ALL',
  NEW = 'NEW',
  TAGS = 'TAGS',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum ProPromoCodeWindowBasis
/**
 * Ce que bornent les dates d'une promotion (lot 4) : la date du rendez-vous (défaut : happy hours, opération
 * sur place) ou la date de réservation (offre vendue sur une période pour des rendez-vous plus tard).
 */
export enum ProPromoCodeWindowBasis {
  APPOINTMENT_AT = 'APPOINTMENT_AT',
  BOOKED_AT = 'BOOKED_AT',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum ProPromoRedemptionSource
/** Origine d'une utilisation : réservation en ligne du client, ou promotion posée par le pro depuis l'agenda. */
export enum ProPromoRedemptionSource {
  CUSTOMER_BOOKING = 'CUSTOMER_BOOKING',
  PRO_AGENDA = 'PRO_AGENDA',
}

/**
 * Statut affiché d'une promotion (cartes et « Codes actifs ») : le statut stocké, et pour une promotion
 * active sa fenêtre et son quota (« à venir », « terminée », « épuisée » ne sont pas stockés).
 */
export enum ProPromotionDisplayStatus {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  ENDED = 'ENDED',
  EXHAUSTED = 'EXHAUSTED',
  ARCHIVED = 'ARCHIVED',
}

// Raison pour laquelle un code promo n'est pas applicable à une réservation.
// Soft en cart-summary (renvoyée sans throw), ferme au pre-reservation.
export enum BookingPromoIneligibilityReason {
  CODE_NOT_FOUND = 'CODE_NOT_FOUND',
  CODE_INACTIVE = 'CODE_INACTIVE',
  OUTSIDE_VALIDITY_WINDOW = 'OUTSIDE_VALIDITY_WINDOW',
  OUTSIDE_HAPPY_HOURS = 'OUTSIDE_HAPPY_HOURS',
  NOT_APPLICABLE_TO_CART = 'NOT_APPLICABLE_TO_CART',
  MIN_CART_AMOUNT_NOT_MET = 'MIN_CART_AMOUNT_NOT_MET',
  NOT_FIRST_TIME_CUSTOMER = 'NOT_FIRST_TIME_CUSTOMER',
  MAX_REDEMPTIONS_REACHED = 'MAX_REDEMPTIONS_REACHED',
  MAX_PER_CUSTOMER_REACHED = 'MAX_PER_CUSTOMER_REACHED',
  PERSONAL_CODE_ALREADY_USED = 'PERSONAL_CODE_ALREADY_USED',
  // Code personnel saisi par un autre client que son destinataire (pré-réservation, email connu).
  PERSONAL_CODE_NOT_FOR_CUSTOMER = 'PERSONAL_CODE_NOT_FOR_CUSTOMER',
  PROMO_VOUCHER_NOT_CUMULABLE = 'PROMO_VOUCHER_NOT_CUMULABLE',
  ZERO_DISCOUNT = 'ZERO_DISCOUNT',
  // Lot 2 : une promotion automatique déjà appliquée donne plus que le code saisi.
  BETTER_OFFER_APPLIED = 'BETTER_OFFER_APPLIED',
  // Promotion réservée aux clients portant certains tags.
  CUSTOMER_NOT_TARGETED = 'CUSTOMER_NOT_TARGETED',
  // Promotion limitée à d'autres lieux que celui du rendez-vous.
  NOT_APPLICABLE_TO_PLACE = 'NOT_APPLICABLE_TO_PLACE',
  // Lot 4 : promotion limitée à d'autres membres que celui du rendez-vous.
  NOT_APPLICABLE_TO_MEMBER = 'NOT_APPLICABLE_TO_MEMBER',
  // Agenda : rendez-vous sans client — une promotion se consomme par un client (utilisation, plafond par
  // client, « nouveaux clients »). Jamais produite par le tunnel.
  NO_CUSTOMER = 'NO_CUSTOMER',
}

export const ProPromoCodeKindTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_KIND}${ProPromoCodeKind.PERCENT}`]: 'Pourcentage',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_KIND}${ProPromoCodeKind.FIXED_AMOUNT_MINOR}`]: 'Montant fixe',
  },
};

validateEnumTranslations(ProPromoCodeKind, ProPromoCodeKindTranslation, TRANSLATE_KEY_PRO_PROMO_CODE_KIND, true);

export const ProPromoCodeKindSchema = (options?: SchemaOptions) => {
  return Type.Union(
    Object.values(ProPromoCodeKind).map((value) =>
      Type.Literal(value, {
        label: `${TRANSLATE_KEY_PRO_PROMO_CODE_KIND}${value}`,
      }),
    ),
    options,
  );
};

export const ProPromoCodeDistributionTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_DISTRIBUTION}${ProPromoCodeDistribution.SHARED_CODE}`]: 'Code partagé',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_DISTRIBUTION}${ProPromoCodeDistribution.PERSONAL_CODES}`]: 'Codes personnels',
  },
};

validateEnumTranslations(
  ProPromoCodeDistribution,
  ProPromoCodeDistributionTranslation,
  TRANSLATE_KEY_PRO_PROMO_CODE_DISTRIBUTION,
  true,
);

export const ProPromoCodeDistributionSchema = (options?: SchemaOptions) => {
  return Type.Union(
    Object.values(ProPromoCodeDistribution).map((value) =>
      Type.Literal(value, {
        label: `${TRANSLATE_KEY_PRO_PROMO_CODE_DISTRIBUTION}${value}`,
      }),
    ),
    options,
  );
};

export const BookingPromoIneligibilityReasonTranslation = {
  'fr-FR': {
    // Neutre : le champ du tunnel accepte un code promo OU une carte cadeau.
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.CODE_NOT_FOUND}`]:
      'Ce code est introuvable',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.CODE_INACTIVE}`]:
      "Ce code promo n'est plus actif",
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.OUTSIDE_VALIDITY_WINDOW}`]:
      "Ce code promo n'est pas encore valide ou a expiré",
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.OUTSIDE_HAPPY_HOURS}`]:
      "Ce code promo n'est valable que certains jours ou à certaines heures",
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_CART}`]:
      "Ce code promo ne s'applique à aucune prestation de votre panier",
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.MIN_CART_AMOUNT_NOT_MET}`]:
      "Le montant minimum requis pour ce code promo n'est pas atteint",
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.NOT_FIRST_TIME_CUSTOMER}`]:
      'Ce code promo est réservé aux nouveaux clients',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.MAX_REDEMPTIONS_REACHED}`]:
      "Ce code promo a atteint son nombre maximum d'utilisations",
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.MAX_PER_CUSTOMER_REACHED}`]:
      'Vous avez déjà utilisé ce code promo le nombre maximum de fois',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.PERSONAL_CODE_ALREADY_USED}`]:
      'Ce code personnel a déjà été utilisé',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.PERSONAL_CODE_NOT_FOR_CUSTOMER}`]:
      'Ce code personnel est lié à un autre client : utilisez l’adresse e-mail à laquelle il a été envoyé',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.PROMO_VOUCHER_NOT_CUMULABLE}`]:
      'Un code promo et une carte cadeau ne sont pas cumulables',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.ZERO_DISCOUNT}`]:
      'Ce code promo ne donne aucune remise sur votre panier',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.BETTER_OFFER_APPLIED}`]:
      'Une meilleure offre est déjà appliquée à votre réservation',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.CUSTOMER_NOT_TARGETED}`]:
      'Cette offre est réservée à certains clients',
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_PLACE}`]:
      "Cette offre n'est pas valable dans ce lieu",
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.NOT_APPLICABLE_TO_MEMBER}`]:
      "Cette offre n'est pas valable avec ce membre de l'équipe",
    [`${TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY}${BookingPromoIneligibilityReason.NO_CUSTOMER}`]:
      'Ce rendez-vous n’a pas de client : rattachez-en un pour appliquer une promotion',
  },
};

validateEnumTranslations(
  BookingPromoIneligibilityReason,
  BookingPromoIneligibilityReasonTranslation,
  TRANSLATE_KEY_BOOKING_PROMO_INELIGIBILITY,
  true,
);

export const ProPromoCodeTriggerTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_TRIGGER}${ProPromoCodeTrigger.AUTO}`]: 'Automatique',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_TRIGGER}${ProPromoCodeTrigger.CODE}`]: 'Avec un code',
  },
};

validateEnumTranslations(
  ProPromoCodeTrigger,
  ProPromoCodeTriggerTranslation,
  TRANSLATE_KEY_PRO_PROMO_CODE_TRIGGER,
  true,
);

export const ProPromoCodeStatusTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_STATUS}${ProPromoCodeStatus.DRAFT}`]: 'Brouillon',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_STATUS}${ProPromoCodeStatus.ACTIVE}`]: 'Active',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_STATUS}${ProPromoCodeStatus.PAUSED}`]: 'En pause',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_STATUS}${ProPromoCodeStatus.ARCHIVED}`]: 'Archivée',
  },
};

validateEnumTranslations(ProPromoCodeStatus, ProPromoCodeStatusTranslation, TRANSLATE_KEY_PRO_PROMO_CODE_STATUS, true);

export const ProPromoCodeCustomerScopeTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_CUSTOMER_SCOPE}${ProPromoCodeCustomerScope.ALL}`]: 'Tous les clients',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_CUSTOMER_SCOPE}${ProPromoCodeCustomerScope.NEW}`]: 'Nouveaux clients',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_CUSTOMER_SCOPE}${ProPromoCodeCustomerScope.TAGS}`]: 'Clients avec un tag',
  },
};

validateEnumTranslations(
  ProPromoCodeCustomerScope,
  ProPromoCodeCustomerScopeTranslation,
  TRANSLATE_KEY_PRO_PROMO_CODE_CUSTOMER_SCOPE,
  true,
);

export const ProPromoCodeWindowBasisTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_WINDOW_BASIS}${ProPromoCodeWindowBasis.APPOINTMENT_AT}`]: 'la date du rendez-vous',
    [`${TRANSLATE_KEY_PRO_PROMO_CODE_WINDOW_BASIS}${ProPromoCodeWindowBasis.BOOKED_AT}`]: 'la date de réservation',
  },
};

validateEnumTranslations(
  ProPromoCodeWindowBasis,
  ProPromoCodeWindowBasisTranslation,
  TRANSLATE_KEY_PRO_PROMO_CODE_WINDOW_BASIS,
  true,
);

export const ProPromotionDisplayStatusTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS}${ProPromotionDisplayStatus.DRAFT}`]: 'Brouillon',
    [`${TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS}${ProPromotionDisplayStatus.SCHEDULED}`]: 'À venir',
    [`${TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS}${ProPromotionDisplayStatus.ACTIVE}`]: 'Active',
    [`${TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS}${ProPromotionDisplayStatus.PAUSED}`]: 'En pause',
    [`${TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS}${ProPromotionDisplayStatus.ENDED}`]: 'Terminée',
    [`${TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS}${ProPromotionDisplayStatus.EXHAUSTED}`]: 'Épuisée',
    [`${TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS}${ProPromotionDisplayStatus.ARCHIVED}`]: 'Archivée',
  },
};

validateEnumTranslations(
  ProPromotionDisplayStatus,
  ProPromotionDisplayStatusTranslation,
  TRANSLATE_KEY_PRO_PROMOTION_DISPLAY_STATUS,
  true,
);
